import { isEmpty, memoize } from 'mojito/utils';
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import PlaceBetContent from 'modules/betslip/betslip-view/place-bet-content/index.js';
import FreeBetsList from 'modules/betslip/betslip-view/free-bets-list/index.js';
import BetslipNotifications from 'modules/betslip-notifications/index.js';
import BetslipSettings from 'modules/betslip/betslip-view/settings/index.js';
import BetslipError from 'modules/betslip/betslip-view/betslip-error/index.js';
import BetslipModuleUtils from 'modules/betslip/utils.js';
import PlaceBetCalculations from 'modules/betslip/betslip-view/place-bet-calculations/index.js';

const { utils: BetslipUtils, types: BetslipTypes } = MojitoServices.Betslip;
const { NOT_COMBINABLE, NOT_ENOUGH_SELECTIONS } = BetslipTypes.ERROR_CODE;
const { AbsolutePane, FlexPane, Text, ScrollPane } = MojitoPresentation.Components;
const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;

const pickPlacementErrors = memoize(BetslipModuleUtils.pickPlacementErrors);

export default class Betslip extends UIViewImplementation {
    constructor(props) {
        super(props);
        this.onBetslipSettingsOpened = this.onBetslipSettingsOpened.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (this.props.betslip && prevProps.betslip) {
            const { errors = [] } = this.props.betslip;
            const { errors: previousErrors = [] } = prevProps.betslip;
            if (errors.length > previousErrors.length) {
                this.props.scrollFullscreenBetslipToBottom();
            }
        }
    }

    onBetslipSettingsOpened() {
        this.emitAnalytics('betslipSettingsOpened');
    }

    renderPlaceBetContent() {
        const {
            betPlacementButtonDisabled,
            betslip,
            currencyCode,
            stakeGroup,
            hasOddsChangeBet,
            hasHcapChangeBet,
            hasBoostedHcapChange,
            isSuspended,
            isExpired,
            betslipStatus,
            validationErrors,
            commitBetslipMode,
        } = this.props;

        const calculation = BetslipUtils.getCalculation(betslip, stakeGroup);
        return (
            <PlaceBetContent
                class="ta-BetslipPlaceBetContent"
                config={this.config.placeBetContent}
                calculation={calculation}
                currencyCode={currencyCode}
                isSuspended={isSuspended || isExpired}
                placeBetButtonDisabled={betPlacementButtonDisabled}
                hasBetslipChanges={hasOddsChangeBet || hasHcapChangeBet || hasBoostedHcapChange}
                placementErrors={pickPlacementErrors(validationErrors)}
                betslipStatus={betslipStatus}
                commitBetslipMode={commitBetslipMode}
                onOpenSettingsCallback={this.onBetslipSettingsOpened}
            />
        );
    }

    renderBetslip() {
        const { betslip, stakeGroup, isSuspended, currencyCode } = this.props;
        const hasContent = BetslipModuleUtils.hasContent(betslip);
        const calculation = BetslipUtils.getCalculation(betslip, stakeGroup);
        const displayAccaBoost = BetslipUtils.stakeGroupHasAccaBoost(betslip, stakeGroup);
        const { boosts } = betslip?.bets?.[0]?.oddsBoostToken || {};
        const bonusInfo = {
            contributionSource: boosts?.[0]?.contributionSource,
            oddsBoostSubtype: boosts?.[0]?.oddsBoostSubtype,
        };
        return (
            <FlexPane config={this.config.container} class={'ta-Betslip'}>
                <ContentWrapper {...this.props}>
                    <FlexPane config={this.config.innerContainer} class="ta-InnerContainer">
                        {this.renderDisabledOverlay()}
                        {this.props.children}
                        {hasContent && this.renderFreeBets()}
                    </FlexPane>
                    {hasContent && (
                        <BetslipNotifications
                            config={this.config.betslipNotifications}
                            errors={this.getErrors()}
                        />
                    )}
                    <PlaceBetCalculations
                        config={this.config.placeBetCalculations}
                        calculation={calculation}
                        isSuspended={isSuspended}
                        calculationsHasAccaBoost={displayAccaBoost}
                        currencyCode={currencyCode}
                        bonusInfo={bonusInfo}
                    />
                </ContentWrapper>
                {hasContent && this.renderPlaceBetContent()}
            </FlexPane>
        );
    }

    getErrors() {
        const { DEFAULT, SINGLES } = BetslipTypes.STAKE_GROUP_NAME;
        const {
            betslip,
            isSuspended,
            isExpired,
            hasOddsChangeBet,
            hasHcapChangeBet,
            hasBoostedHcapChange,
            validationErrors,
            stakeGroup = DEFAULT,
        } = this.props;
        const errors = BetslipModuleUtils.getErrors(
            betslip,
            isSuspended,
            hasOddsChangeBet,
            hasHcapChangeBet,
            hasBoostedHcapChange,
            isExpired,
            validationErrors
        );

        const isSinglesSelected = stakeGroup === SINGLES;
        const isDefaultSelected = stakeGroup === DEFAULT;
        const isOneSelectionOnNonSingleGroup = BetslipUtils.getSingleBetsCount(betslip) === 1;
        const isOneBBSelectionOnNonSingleGroup = BetslipUtils.isValidBetBuilderMultiple(betslip);

        if (
            !isSinglesSelected &&
            !isDefaultSelected &&
            (isOneSelectionOnNonSingleGroup || isOneBBSelectionOnNonSingleGroup)
        ) {
            const message = this.resolveString(`$BETSLIP.EMPTY_TAB.${stakeGroup.toUpperCase()}`);
            errors.push({ code: NOT_ENOUGH_SELECTIONS, message });
        }

        if (BetslipUtils.hasNotCombinableBets(betslip) && !isSinglesSelected) {
            errors.push({ code: NOT_COMBINABLE });
        }

        return errors;
    }

    renderEmptyBetslip() {
        return (
            <FlexPane class="ta-EmptyBetslip" config={this.config.container}>
                <FlexPane config={this.config.emptyBetslipTextContainer}>
                    <Text config={this.config.emptyBetslipText} class="ta-EmptyBetslipText">
                        {this.resolveString('$BETSLIP.EMPTY_BETSLIP')}
                    </Text>
                </FlexPane>
                <BetslipNotifications
                    config={this.config.betslipNotifications}
                    errors={this.getErrors()}
                />
                {this.config.showBetslipSettingsInEmptyBetslip && (
                    <FlexPane config={this.config.betslipSettingsContainer}>
                        <BetslipSettings
                            config={this.config.betslipSettings}
                            onOpenCallback={this.onBetslipSettingsOpened}
                        />
                    </FlexPane>
                )}
            </FlexPane>
        );
    }

    renderDisabledOverlay() {
        if (!BetslipUtils.isPending(this.props.betslipStatus)) {
            return null;
        }
        return <AbsolutePane class="ta-DisabledOverlay" config={this.config.disabledOverlay} />;
    }

    renderFreeBets() {
        const { freeBets, betslip, stakeGroup } = this.props;
        if (isEmpty(freeBets)) {
            return null;
        }
        const freeBetConstraints = BetslipUtils.getFreeBetConstraints(betslip, stakeGroup);
        return (
            <FreeBetsList
                freeBets={freeBets}
                betslip={betslip}
                freeBetConstraints={freeBetConstraints}
            />
        );
    }

    getTechnicalErrorMessage() {
        const { errors = [] } = this.props.betslip;
        const technicalFailureError = errors[0] || {};
        const { message, code } = technicalFailureError;
        return message
            ? `${message}. ${code}`
            : this.resolveAndFormatString(
                  '$BETSLIP_NOTIFICATIONS.MESSAGES.TECHNICAL_FAILURE',
                  code
              );
    }

    render() {
        const { betslipStatus, betslip, onAcceptTechnicalError } = this.props;
        if (BetslipUtils.isFailed(betslipStatus)) {
            const message = this.getTechnicalErrorMessage();
            return <BetslipError message={message} onOkClick={onAcceptTechnicalError} />;
        }
        const isBetslipEmpty = BetslipUtils.isBetslipEmpty(betslip);
        if (isBetslipEmpty) {
            return this.renderEmptyBetslip();
        }
        return this.renderBetslip();
    }
}

const ContentWrapper = props => {
    const {
        mojitoTools: { config },
        children,
        scrollPaneProps,
    } = props;
    return config.isScrollable ? (
        <ScrollPane config={config.scrollableContent} {...scrollPaneProps}>
            {children}
        </ScrollPane>
    ) : (
        children
    );
};
